/* /saudization — port of prototype pgSaudization (pages2.js). */
import { useState } from "react";
import { useAllEmployees, useAuthenticatePending, useSaudization } from "../../data/hooks";
import { BANDS, bandOf, saudisNeeded } from "../../domain/compliance";
import { useI18n, LOOKUP } from "../../i18n";
import { AiNote, Alert, Bar, Button, Card, Chip, Donut, ErrorBox, Gauge, Kv, PageHeader, Skeleton, errMsg, useToast } from "../../ui";
import type { CountryInfo } from "../../domain/country";
import { useCountry } from "../../lib/country";
import "./core.css";

const MEANING: [string, string][] = [
  ["Visas and most Qiwa services blocked", "إيقاف التأشيرات ومعظم خدمات قوى"],
  ["Limited services", "خدمات محدودة"],
  ["Standard services", "خدمات اعتيادية"],
  ["Most services and visa approvals", "معظم الخدمات والتأشيرات"],
  ["All services, fastest processing", "جميع الخدمات وأسرع معالجة"],
];

/** Saudi Arabia: Nitaqat band, Qiwa documentation and the hiring simulator. */
function SaudiNitaqat() {
  const i = useI18n();
  const { t, L } = i;
  const toast = useToast();
  const [sa, setSa] = useState(0);
  const [ns, setNs] = useState(0);
  const baseQ = useSaudization();
  const simQ = useSaudization(sa, ns, { placeholderData: (p) => p });
  const empsQ = useAllEmployees();
  const auth = useAuthenticatePending();

  const err = baseQ.error || empsQ.error;
  if (err) return <ErrorBox error={err} onRetry={() => { baseQ.refetch(); empsQ.refetch(); }} />;
  if (!baseQ.data || !empsQ.data) return <Skeleton />;

  const nq = baseQ.data;
  const emps = empsQ.data;
  const act = emps.filter((e) => e.status !== "Terminated");
  const band = bandOf(nq.pct);
  const bi = BANDS.findIndex((b) => b.key === band.key);
  const nextB = BANDS[bi + 1];
  const need = nextB ? saudisNeeded(emps, band.to) : 0;
  const saCount = act.filter((e) => e.nationality === "SA").length;
  const nsCount = act.length - saCount;
  const sim = simQ.data ?? nq;
  const pb = bandOf(sim.pct);
  const pbi = BANDS.findIndex((b) => b.key === pb.key);

  const stepper = (label: string, v: number, set: (n: number) => void, key: string) => (
    <div className="list-row">
      <span className="grow">{label}</span>
      <div className="row nw">
        <Button variant="sm" icon="minus" aria-label={label + " −1"} disabled={v <= 0} onClick={() => set(Math.max(0, v - 1))} data-testid={"sim-" + key + "-minus"} />
        <span className="num core-sim-n" data-testid={"sim-" + key}>{v}</span>
        <Button variant="sm" icon="plus" aria-label={label + " +1"} onClick={() => set(v + 1)} data-testid={"sim-" + key + "-plus"} />
      </div>
    </div>
  );

  const authenticate = async () => {
    try {
      const r = await auth.mutateAsync(undefined);
      toast(L("Contracts marked authenticated · Saudization now " + r.pct.toFixed(1) + "%", "تم توثيق العقود · نسبة التوطين الآن " + r.pct.toFixed(1) + "%"));
    } catch (e) { toast(errMsg(e), "bad"); }
  };

  return (
    <div data-testid="saudization">
      <PageHeader
        title={t("Saudization")}
        sub={L("Nitaqat status on Qiwa · Wholesale & retail trade · small entity", "حالة نطاقات في قوى · تجارة الجملة والتجزئة · منشأة صغيرة")}
        actions={<Button icon="refresh" onClick={() => toast(L("Qiwa has no public API yet — figures are calculated from your employee records. Compare with Qiwa › Nitaqat.", "لا توفر قوى واجهة برمجية عامة بعد — تُحسب الأرقام من بيانات موظفيك. قارنها بخدمة نطاقات في قوى."), "info")} data-testid="btn-sync">{L("Sync with Qiwa", "مزامنة مع قوى")}</Button>}
      />
      <div className="g g-3 mb">
        <Card title={t("Current band")} testId="current-band">
          <Gauge pct={nq.pct} bands={BANDS} />
          <div className="row between"><Chip status={band.key} tone={band.tone} testId="band-chip" /><span className="sm muted">{saCount} / {act.length}</span></div>
          {nextB && need > 0 && (
            <p className="sm" style={{ margin: "12px 0 0" }} data-testid="need-next">
              {L("Hire ", "وظّف ")}<b className="num">{need}</b>{L(" more Saudi " + (need === 1 ? "national" : "nationals") + " to reach " + nextB.key + ".", " سعوديين إضافيين للوصول إلى " + t(nextB.key) + ".")}
            </p>
          )}
        </Card>
        <Card title={L("Saudi vs non-Saudi", "سعودي وغير سعودي")} testId="split">
          <div className="row nw" style={{ gap: 20, flexWrap: "wrap" }}>
            <Donut parts={[[t("Saudi"), saCount, "var(--chart-1)"], [t("Non-Saudi"), nsCount, "var(--chart-4)"]]} center={(act.length ? (saCount / act.length) * 100 : 0).toFixed(0) + "%"} sub={t("Saudi")} />
            <div className="stack grow" style={{ minWidth: 140 }}>
              {Object.keys(LOOKUP.DEPT).map((k) => {
                const a = act.filter((e) => e.dept === k);
                const s = a.filter((e) => e.nationality === "SA").length;
                return <div key={k} data-testid={"dept-split-" + k}><div className="row between xs" style={{ marginBottom: 4 }}><span>{i.dept(k)}</span><span className="num">{s}/{a.length}</span></div><Bar value={s} max={a.length} /></div>;
              })}
            </div>
          </div>
        </Card>
        <Card title={t("Hiring simulator")} sub={t("What if I hire…")} testId="simulator">
          <div className="stack">
            {stepper(t("Saudi hires"), sa, setSa, "sa")}
            {stepper(t("Non-Saudi hires"), ns, setNs, "ns")}
            <div className="divider" />
            <div className="row between"><span className="muted sm">{t("Projected band")}</span><span className="row nw"><Chip status={pb.key} tone={pb.tone} testId="projected-band" /><b className="num" data-testid="projected-pct">{sim.pct.toFixed(1)}%</b></span></div>
            {pb.key !== band.key && (
              <Alert tone={pbi > bi ? "ok" : "warn"} tint testId="sim-alert"
                title={pbi > bi ? L("You'd move up to " + pb.key, "ستنتقل إلى " + t(pb.key)) : L("You'd drop to " + pb.key, "ستنخفض إلى " + t(pb.key))}
                desc={L("Bands unlock or restrict visas and Qiwa services.", "النطاقات تفتح أو تقيد التأشيرات وخدمات قوى.")} />
            )}
            <Button variant="ghost sm" onClick={() => { setSa(0); setNs(0); }} disabled={!sa && !ns} data-testid="sim-reset">{L("Reset", "إعادة ضبط")}</Button>
          </div>
        </Card>
      </div>
      <div className="g g-2 mb">
        <Card title={L("How your rate is counted", "كيف تُحسب النسبة")} testId="how-counted">
          <Kv items={[
            [L("Saudis on payroll", "السعوديون في الرواتب"), <span className="num">{nq.raw}</span>],
            [L("Not counted · contract not authenticated on Qiwa", "غير محتسبين · عقد غير موثق في قوى"), nq.notDoc ? <Chip tone="warn" nodot testId="not-doc">{nq.notDoc}</Chip> : <span className="num" data-testid="not-doc">0</span>],
            [L("Counted as half · salary under SAR 4,000", "يُحتسب نصفًا · راتب أقل من 4,000"), <span className="num">{nq.under}</span>],
            [L("Weighted Saudi count", "العدد الموزون"), <><b className="num" data-testid="weighted">{nq.weighted.toFixed(1)}</b> / {nq.n}</>],
          ]} />
          {nq.notDoc > 0 && <div className="mt"><AiNote>{L("Authenticating the " + nq.notDoc + " pending Saudi contracts adds " + nq.notDoc + " to your count — the quickest way up.", "توثيق " + nq.notDoc + " عقود سعودية معلقة يضيف " + nq.notDoc + " إلى العدد، وهي أسرع طريقة للارتفاع.")}</AiNote></div>}
        </Card>
        <Card title={L("Qiwa contract documentation", "توثيق العقود في قوى")} testId="qiwa-doc">
          <div className="kpi-v"><span data-testid="doc-pct">{nq.docPct.toFixed(1)}</span><span className="kpi-u">% · {nq.docd} / {nq.n}</span></div>
          <Bar value={nq.docPct} max={100} tone={nq.docPct >= 90 ? "" : "warn"} />
          <p className="sm muted" style={{ margin: "10px 0 0" }}>{L("Target: 90% of contracts authenticated (since 30 Jun 2026). From 15 Apr 2026 only authenticated Saudi contracts count toward Nitaqat.", "المستهدف: توثيق 90% من العقود منذ 30 يونيو 2026، ومنذ 15 أبريل 2026 تُحتسب العقود السعودية الموثقة فقط.")}</p>
          <p className="xs muted" style={{ margin: "6px 0 0" }}>{L("Qiwa has no API: employees accept the contract in Qiwa; mark them authenticated here once done.", "لا توفر قوى واجهة برمجية: يوافق الموظف على العقد في قوى، ثم سجّل التوثيق هنا.")}</p>
          <div className="mt"><Button variant="sm pri" icon="refresh" loading={auth.isPending} disabled={nq.notDoc === 0} onClick={authenticate} data-testid="btn-authenticate">{L("Authenticate pending contracts", "توثيق العقود المعلقة")}</Button></div>
        </Card>
      </div>
      <Card title={L("Band thresholds", "حدود النطاقات")} sub={L("Illustrative thresholds — real bands depend on your activity and entity size on Qiwa", "حدود توضيحية — النطاقات الفعلية تعتمد على نشاط المنشأة وحجمها في قوى")} flush testId="bands">
        <div className="tw">
          <table className="tbl">
            <thead><tr><th>{L("Band", "النطاق")}</th><th className="end">{L("Saudi % from", "النسبة من")}</th><th className="end">{L("to", "إلى")}</th><th>{L("What it means", "ماذا يعني")}</th></tr></thead>
            <tbody>
              {BANDS.map((b, k) => (
                <tr key={b.key} className={b.key === band.key ? "sel" : ""} data-testid={"band-row-" + k}>
                  <td><Chip status={b.key} tone={b.tone} /></td>
                  <td className="end num">{k ? BANDS[k - 1].to : 0}%</td>
                  <td className="end num">{b.to >= 100 ? "—" : b.to + "%"}</td>
                  <td className="sm">{L(MEANING[k][0], MEANING[k][1])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/** /saudization: Nitaqat in Saudi Arabia; elsewhere the country's localisation programme (Emiratisation, Omanisation…). */
export default function Saudization() {
  const c = useCountry();
  return c.code === "SA" ? <SaudiNitaqat /> : <Localisation c={c} />;
}

/** Citizens on the payroll against the country's localisation rules (no Saudi bands outside Saudi Arabia). */
function Localisation({ c }: { c: CountryInfo }) {
  const i = useI18n();
  const { L } = i;
  const empsQ = useAllEmployees();
  if (empsQ.error) return <ErrorBox error={empsQ.error} onRetry={() => { empsQ.refetch(); }} />;
  if (!empsQ.data) return <Skeleton />;
  const act = empsQ.data.filter((e) => e.status !== "Terminated");
  const cit = act.filter((e) => e.nationality === c.code).length;
  const pct = act.length ? (cit / act.length) * 100 : 0;
  const byNat = Object.entries(act.reduce<Record<string, number>>((m, e) => ({ ...m, [e.nationality]: (m[e.nationality] ?? 0) + 1 }), {})).sort((a, b) => b[1] - a[1]);
  return (
    <div data-testid="localisation">
      <PageHeader title={L(...c.localisation.name)} sub={L(`Citizens on your payroll in ${c.short[0]}`, `المواطنون في رواتبك في ${c.short[1]}`)} />
      <div className="g g-3 mb">
        <Card title={L(`${c.demonym[0]} share`, `نسبة ال${c.demonym[1]}ين`)} testId="loc-share">
          <div className="kpi-v"><span data-testid="loc-pct">{pct.toFixed(1)}</span><span className="kpi-u">% · {cit} / {act.length}</span></div>
          <Bar value={pct} max={100} />
          <p className="sm muted" style={{ margin: "10px 0 0" }}>{L(`${c.demonym[0]} nationals count toward ${c.localisation.short[0]}; everyone else is counted as an expatriate.`, `يُحتسب المواطنون ال${c.demonym[1]}ون ضمن ${c.localisation.short[1]}، ويُحتسب غيرهم وافدين.`)}</p>
        </Card>
        <Card title={L("Citizens and expatriates", "المواطنون والوافدون")} testId="loc-split">
          <Donut parts={[[L(c.demonym[0], c.demonym[1]), cit, "var(--chart-1)"], [L("Expatriate", "وافد"), act.length - cit, "var(--chart-4)"]]} center={pct.toFixed(0) + "%"} sub={L(c.demonym[0], c.demonym[1])} />
        </Card>
        <Card title={L("By nationality", "حسب الجنسية")} testId="loc-nat">
          <div className="stack">
            {byNat.slice(0, 6).map(([n, k]) => <div key={n}><div className="row between xs" style={{ marginBottom: 4 }}><span>{i.nat(n)}</span><span className="num">{k}</span></div><Bar value={k} max={act.length} /></div>)}
          </div>
        </Card>
      </div>
      <Card title={L("What the rules ask of you", "ما تطلبه القواعد منك")} testId="loc-rules">
        <p style={{ margin: 0 }}>{L(...c.localisation.summary)}</p>
        <p className="sm muted" style={{ margin: "10px 0 0" }}>{L(`StartHR calculates the share from your employee records. Check your official status on the ${c.localisation.name[0]} portal.`, `يحسب ستارت HR النسبة من سجلات موظفيك. تحقق من وضعك الرسمي في بوابة ${c.localisation.name[1]}.`)}</p>
        <div className="mt"><a className="btn sm" href={c.localisation.url} target="_blank" rel="noreferrer" data-testid="loc-link">{L(`Open ${c.localisation.short[0]} portal`, `فتح بوابة ${c.localisation.short[1]}`)}</a></div>
      </Card>
    </div>
  );
}

